import { useEffect, useState, useRef, useCallback } from "react";
import api from "../../api/client";
import useAuth from "../../hooks/useAuth";
import MigrationDetailModal from "./MigrationDetailModal";

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-PH", {
    timeZone: "Asia/Manila",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function MigrationChatModal({ recordId, onClose, onCountUpdate, notifRef }) {
  const { user, hasPerm } = useAuth();
  const canPost = hasPerm("chat.post");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const threadRef = useRef(null);
  const userScrolledUp = useRef(false);
  const prevMsgCount = useRef(0);

  const fetchMessages = useCallback(async () => {
    try {
      const { data: res } = await api.get(`/migration/${recordId}/messages`);
      setData(res);
      if (onCountUpdate) onCountUpdate(recordId, res.messages.length);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [recordId, onCountUpdate]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Poll the SSE notifRef for events matching this record
  useEffect(() => {
    if (!notifRef) return;
    const interval = setInterval(() => {
      const ev = notifRef.current;
      if (ev && ev.migration_record_id === recordId) {
        notifRef.current = null;
        fetchMessages();
      }
    }, 500);
    return () => clearInterval(interval);
  }, [notifRef, recordId, fetchMessages]);

  // Track whether user has scrolled up
  useEffect(() => {
    const el = threadRef.current;
    if (!el) return;
    const handleScroll = () => {
      const threshold = 150;
      userScrolledUp.current = el.scrollHeight - el.scrollTop - el.clientHeight > threshold;
    };
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  // Auto-scroll to bottom only on initial load, user's own send, or if already at bottom
  useEffect(() => {
    if (!threadRef.current || !data?.messages) return;
    const isInitialLoad = prevMsgCount.current === 0;
    prevMsgCount.current = data.messages.length;
    if (isInitialLoad || !userScrolledUp.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [data?.messages]);

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    userScrolledUp.current = false;
    setSending(true);
    try {
      const { data: msg } = await api.post(`/migration/${recordId}/messages`, {
        message_text: text.trim(),
      });
      setText("");
      setData((prev) => prev ? {
        ...prev,
        messages: [...prev.messages, msg],
      } : prev);
      if (onCountUpdate && data) onCountUpdate(recordId, (data.messages?.length || 0) + 1);
    } catch {
      // ignore
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 flex flex-col" style={{ maxHeight: "85vh" }}>
        {/* Header — clickable to open Migration details */}
        <div className="px-5 py-3.5 border-b flex items-center justify-between shrink-0">
          <button
            type="button"
            onClick={() => setShowDetail(true)}
            className="min-w-0 text-left group"
            title="View Migration Record Details"
          >
            <h2 className="text-base font-semibold text-gray-900 truncate group-hover:text-blue-700 transition-colors">
              Chat — {data?.oldacctno || `Record #${recordId}`}
              <svg className="inline-block w-3.5 h-3.5 ml-1.5 text-gray-400 group-hover:text-blue-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
            </h2>
            <p className="text-xs text-gray-500 truncate mt-0.5">
              {data?.name || ""}
            </p>
          </button>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none ml-3">&times;</button>
        </div>

        {/* Messages thread */}
        <div ref={threadRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3 min-h-[200px]">
          {loading ? (
            <p className="text-center text-sm text-gray-400 py-8">Loading messages…</p>
          ) : data?.messages?.length === 0 ? (
            <p className="text-center text-sm text-gray-400 py-8">No messages yet. Start the conversation.</p>
          ) : (
            data?.messages?.map((msg) => {
              const isOwn = msg.posted_by_user_id === user?.id;
              return (
                <div
                  key={msg.id}
                  className={`flex ${isOwn ? "justify-end" : "justify-start"}`}
                >
                  <div className={`max-w-[80%] rounded-xl px-3.5 py-2.5 ${
                    isOwn
                      ? "bg-blue-600 text-white rounded-br-sm"
                      : "bg-gray-100 text-gray-900 rounded-bl-sm"
                  }`}>
                    {!isOwn && (
                      <p className={`text-xs font-semibold mb-0.5 ${isOwn ? "text-blue-100" : "text-blue-700"}`}>
                        {msg.posted_by_name}
                        {msg.posted_by_role && (
                          <span className={`font-normal ml-1 ${isOwn ? "text-blue-200" : "text-gray-400"}`}>
                            · {msg.posted_by_role}
                          </span>
                        )}
                      </p>
                    )}
                    <p className="text-sm whitespace-pre-wrap break-words">{msg.message_text}</p>
                    <p className={`text-[10px] mt-1 ${isOwn ? "text-blue-200" : "text-gray-400"}`}>
                      {formatTime(msg.created_at)}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Input area */}
        {canPost && (
          <div className="px-5 py-3 border-t flex gap-2 shrink-0">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message…"
              rows={1}
              className="flex-1 border rounded-lg px-3 py-2 text-sm text-gray-900 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 max-h-24"
              style={{ minHeight: "38px" }}
            />
            <button
              onClick={handleSend}
              disabled={!text.trim() || sending}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
            >
              {sending ? "…" : "Send"}
            </button>
          </div>
        )}
        {!canPost && (
          <div className="px-5 py-3 border-t text-center text-xs text-gray-400 shrink-0">
            You do not have permission to post messages.
          </div>
        )}
      </div>

      {/* Migration Detail modal — rendered at higher z-index to stack above chat */}
      {showDetail && (
        <div className="relative z-[70]">
          <MigrationDetailModal
            recordId={recordId}
            onClose={() => setShowDetail(false)}
          />
        </div>
      )}
    </div>
  );
}
